import Overlay from 'components/ui/Overlay/Overlay';
import { ServiceTypes } from 'constants/ServiceTypes';
import { SessionType } from 'enums/SessionType';
import { History } from 'history';
import { ISessionUpdateRequest } from 'interfaces/review/session/Session';
import { IApiSessionMetadataUpdated } from 'interfaces/tagging/ApiModels/IApiSessionMetadataUpdated';
import { observer } from 'mobx-react-lite';
import { SessionReviewRowManager } from 'models/review/SessionReviewRowManager';
import { MissingOwningOrgModal } from 'pages/Review/Components/MissingOwningOrgModal/MissingOwningOrgModal';
import SubmitModal from 'pages/Review/Components/SubmitModal/SubmitModal';
import TopBar from 'pages/Review/Components/TopBar/TopBar';
import React, { useEffect, useRef, useState } from 'react';
import { useInjection } from 'services/ioc/ioc.react';
import { GameReviewStore } from 'stores/GameReviewStore';
import NoteStore from 'stores/NoteStore';
import { SessionStore } from 'stores/SessionStore';
import { v1 as uuid } from 'uuid';
import { ReviewDataRow } from '../../../models/review/ReviewDataRow/ReviewDataRow';
import { BpPlaysGrid } from './BpPlaysGrid';

interface IProps {
  match?: any;
  history: History;
}
const BpSessionReview: React.FC<IProps> = observer((props): React.ReactElement => {
  const rowManagerRef = useRef<SessionReviewRowManager>(new SessionReviewRowManager());
  const reviewStore = useInjection<GameReviewStore>(ServiceTypes.GameReviewStore);
  const sessionStore = useInjection<SessionStore>(ServiceTypes.SessionStore);
  const { match, history } = props;
  const { id } = match.params;
  const [shouldUpdate, setShouldUpdate] = useState<string>('');
  const [isDisplayingWarmups, setIsDisplayingWarmups] = useState<boolean>(rowManagerRef.current.isDisplayingWarmups);
  const [missingOrgModalVisible, setMissingOrgModalVisible] = useState<boolean>(false);
  const [owningOrgId, setOwningOrgId] = useState<string | undefined>();
  const [sessionSettingsChanged, setSessionSettingsChanged] = useState<boolean>(false);
  const rowManager = rowManagerRef.current;
  const noteStore = useInjection<NoteStore>(ServiceTypes.NoteStore);

  useEffect(() => {
    window.onpopstate = () => {
      history.replace('/');
    };
  }, [window]);

  useEffect(() => {
    initializeData();
    return () => {
      reviewStore.setIsReviewing(false);
    };
  }, []);

  function getRows(): ReviewDataRow[] {
    return rowManager.rows;
  }

  const initializeData = async () => {
    reviewStore.setIsReviewing(true);
    // setScrollBarWidth(getScrollBarWidth());
    await reviewStore.setReviewGame(id);
    const { sessionMetadata, sessionId } = reviewStore.reviewGame;
    setOwningOrgId(sessionMetadata.owningOrganizationId);
    if (sessionId && sessionMetadata.sessionType !== SessionType.BattingPractice) {
      history.push(`/review/${sessionId}`);
    }
    rowManager.onGridUpdate = () => {
      setShouldUpdate(uuid());
    };
  };

  const onToggleWarmupsChanged = (newVal: boolean) => {
    setIsDisplayingWarmups(newVal);
    rowManager.setIsDisplayingWarmups(newVal);
  };

  const onSave = async () => {
    const rowsCpy = getRows().map((r) => r.clone()); // copy data
    getRows().forEach((r) => {
      r.isChanged = false;
    }); // assume update was successful
    rowManager.cellCacheManager.rerenderAllCells();
    rowManager.updateGrid();
    // we cannot update isChanged after save is completed, because tagger could change something while his previous changes are being saved
    const { changedIds, isSuccessful } = await reviewStore.savePlays(rowsCpy);
    if (!isSuccessful) {
      // if update failed, revert isChanged changes
      getRows().forEach((r) => {
        r.isChanged = r.isChanged || !!changedIds.includes(r.id);
      });
      rowManager.cellCacheManager.rerenderAllCells();
      rowManager.updateGrid();
    }
    await sessionStore.updateSessionMetadata(reviewStore.reviewGame.sessionMetadata, true);

    noteStore.setNote('');
    setSessionSettingsChanged(false);
  };

  const onSubmitClickHandler = async () => {
    if (!owningOrgId) {
      setMissingOrgModalVisible(true);
      return;
    }
    reviewStore.setIsSubmitModalVisible(true);
  };

  const onSessionUpdatedHandler = (newSession: ISessionUpdateRequest) => {
    setOwningOrgId(newSession.owningOrganizationId);
    setSessionSettingsChanged(true);
    reviewStore.reviewGame.sessionMetadata = {
      ...reviewStore.reviewGame.sessionMetadata,
      ...{
        owningOrganizationId: newSession.owningOrganizationId,
        externalSessionId: newSession.externalSessionId,
      },
    } satisfies IApiSessionMetadataUpdated;
  };

  const recalculateAndSaveBeforeSubmit = async () => {
    rowManager.recalculateComputedProperties();
    rowManager.autogenerateMissingTimestamps(rowManager.rows);
    const rowsCpy = rowManager.rows.map((r) => r.clone());
    await reviewStore.savePlays(rowsCpy);
    await sessionStore.updateSessionMetadata(reviewStore.reviewGame.sessionMetadata, true);
  };

  const onSubmit = async (): Promise<void> => {
    await recalculateAndSaveBeforeSubmit();
    await reviewStore.submitReviewGame(id);
    reviewStore.setIsSubmitModalVisible(false);
    history.push('/archives');
  };

  const [isSettingsModalVisible, setSettingsModalVisible] = React.useState(false);

  return (
    <div>
      {shouldUpdate && reviewStore.isLoading && <Overlay hasSpinner />}
      <TopBar
        homeTeam={reviewStore.reviewGame.sessionMetadata.homeTeam}
        awayTeam={reviewStore.reviewGame.sessionMetadata.awayTeam}
        taggedDate={reviewStore.reviewGame.taggedOn}
        stadium={reviewStore.reviewGame.field}
        onSave={() => onSave()}
        areUnsavedChanges={getRows().some((r) => r.isChanged) || sessionSettingsChanged}
        isSaving={reviewStore.isSaving}
        onSubmitClickHandler={onSubmitClickHandler}
        isLoading={reviewStore.isLoading}
        shouldDisplayWarmups={isDisplayingWarmups}
        shouldDisplayWarmupsCallback={onToggleWarmupsChanged}
        shouldUpdate={shouldUpdate}
        sessionMetadata={reviewStore.reviewGame.sessionMetadata}
        rowManager={rowManagerRef.current}
        isSettingsModalVisible={isSettingsModalVisible}
        setSettingsModalVisible={setSettingsModalVisible}
        onSessionUpdated={onSessionUpdatedHandler}
      />
      <BpPlaysGrid session={reviewStore.reviewGame} rowManager={rowManagerRef.current} />
      <MissingOwningOrgModal
        isOpen={missingOrgModalVisible}
        onDismiss={() => setMissingOrgModalVisible(false)}
        submitWithoutOwningOrg={() => onSubmit()}
        openSettingsModal={() => {
          setMissingOrgModalVisible(false);
          setSettingsModalVisible(true);
        }}
      />
      <SubmitModal
        history={history}
        id={id}
        errorCount={
          getRows().length > 0
            ? getRows()
                .map((r) => r.status.errors.length)
                .reduce((acc, num) => acc + num)
            : 0
        }
        rowManager={rowManagerRef.current}
        onSubmit={onSubmit}
      />
    </div>
  );
});

export default BpSessionReview;
